// src/pages/NewDocument.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Upload, FileText,
  Clock, CheckCircle, Zap, Plus, Eye
} from 'lucide-react';
import { 
  invalidateDocumentQueries, 
  optimisticallyUpdateDocumentStatus 
} from '../utils/queryClient';
import { documentCacheService } from '../services/documentCacheService';
import DocumentUploader from '../components/DocumentUploader';
import SignatureFieldPlacement from '../components/SignatureFieldPlacement';
import ReviewAndPresign from '../components/ReviewAndPresign';

const NewDocument = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // In development, start with method selection; in production, start with upload
  const ENV = process.env.REACT_APP_ENV || 'development';
  console.log(`NewDocument: Using ${ENV} environment, initialStep: ${ENV === 'development' ? 'method-selection' : 'upload'}`);
  const initialStep = ENV === 'development' ? 'method-selection' : 'upload';
  const [currentStep, setCurrentStep] = useState(initialStep); // 'method-selection', 'upload', 'signature-fields', 'review', 'complete'
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fieldsData, setFieldsData] = useState(null); // Store signature and form fields data
  const [message, setMessage] = useState(''); // Message for signers

  // Build steps array conditionally based on environment
  const getSteps = () => {
    const baseSteps = [
      { id: 'upload', title: 'Upload Document', icon: Upload, status: 'upcoming' },
      { id: 'signature-fields', title: 'Place Fields & Signatures', icon: FileText, status: 'upcoming' },
      { id: 'review', title: 'Review & Presign', icon: Eye, status: 'upcoming' }
    ];

    if (ENV === 'development') {
      return [
        { id: 'method-selection', title: 'Choose Method', icon: Plus, status: 'current' },
        ...baseSteps
      ];
    }

    return baseSteps.map((step, index) => 
      index === 0 ? { ...step, status: 'current' } : step
    );
  };

  const steps = getSteps();

  const getStepStatus = (stepId) => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    
    if (stepIndex < currentIndex) return 'complete';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const handleUploadSuccess = (documentData) => {
    console.log('📤 Document uploaded successfully:', documentData);
    setUploadedDocument(documentData);
    setCurrentStep('signature-fields');
    
    // ✅ CACHE INVALIDATION: Already handled in documentService.uploadDocument, but add extra for safety
    invalidateDocumentQueries();
    console.log('🔄 Cache invalidated after document upload in NewDocument');
  };


  const handleSignatureFieldsComplete = async (fieldsData) => {
    console.log('Fields completed:', fieldsData);
    
    // Store fields data and move to review step
    setFieldsData(fieldsData);
    setCurrentStep('review');
  };

  const handleReviewComplete = async (finalData) => {
    console.log('Review completed:', finalData);
    
    // finalData now contains both signatureFields and formFields, plus title, message, etc.
    const { 
      signatureFields = [], 
      formFields = [], 
      title,
      message: reviewMessage,
      expirationDate,
      saveAsTemplate 
    } = finalData;
    
    setCurrentStep('complete');
    
    try {
      setIsProcessing(true);
      
      // Import signatureService
      const { signatureService } = await import('../services/signatureService');
      
      // Create signature request if there are signature fields
      if (signatureFields.length > 0) {
        // Format the signature fields data for the API - group fields by assignedTo contact
        const signerMap = new Map();
        
        signatureFields.forEach(field => {
          const assignedContact = field.assignedTo;
          if (!assignedContact) return;
          
          const contactKey = assignedContact.email;
          if (!signerMap.has(contactKey)) {
            signerMap.set(contactKey, {
              email: assignedContact.email,
              name: assignedContact.name,
              signatureFields: []
            });
          }
          
          signerMap.get(contactKey).signatureFields.push({
            id: field.id,
            type: field.type,
            page: field.page,
            x: field.x,
            y: field.y,
            width: field.width,
            height: field.height,
            required: field.required
          });
        });
        
        const signers = Array.from(signerMap.values());
        console.log('Formatted signers for API:', signers);
        
        // Include all review data
        const requestData = { 
          signers,
          title: title || uploadedDocument.name,
          message: reviewMessage,
          expirationDate
        };
        
        const result = await signatureService.createSignatureRequest(uploadedDocument.id, requestData.signers, requestData.message, requestData);
        console.log('📋 Signature request created:', result);
      }
      
      // Save form fields if any were added
      if (formFields.length > 0) {
        const { formFieldService } = await import('../services/formFieldService');
        await formFieldService.createFormFields(uploadedDocument.id, formFields);
        console.log('📋 Form fields saved:', formFields);
      }
      
      // ✅ CACHE INVALIDATION
      invalidateDocumentQueries();
      
      // 🚀 OPTIMISTIC UPDATE: Update document status
      const status = signatureFields.length > 0 ? 'pending_signature' : 'completed';
      optimisticallyUpdateDocumentStatus(uploadedDocument.id, status);
      
      // 🚀 Smart cache tracking for document status change
      if (status === 'pending_signature') {
        documentCacheService.addPendingDocument(uploadedDocument.id);
      }
      documentCacheService.invalidateDocumentCache(uploadedDocument.id, status);
      
      console.log('🔄 Cache invalidated after document creation in NewDocument');
      
      // Navigate back to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error finalizing document:', error);
      alert('Failed to finalize document. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };


  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Method selection handlers (development only)
  const handleMethodSelection = (method) => {
    if (method === 'upload') {
      setCurrentStep('upload');
    } else if (method === 'generate') {
      setCurrentStep('generate-preview');
    }
  };

  // Upload option handlers
  const handleUploadOption = (option) => {
    if (option === 'regular') {
      setCurrentStep('signature-fields');
    } else if (option === 'autoplace') {
      handleAutoPlaceFields();
    }
  };

  // Handle auto-placing fields
  const handleAutoPlaceFields = async () => {
    if (!uploadedDocument) return;

    try {
      setIsProcessing(true);

      // Import the enhanced auto field service
      const { enhancedAutoFieldService } = await import('../services/enhancedAutoFieldService');

      // Use the coordinate-based detection for better field placement
      const ocrAutofillService = await import('../services/ocrAutofillService');
      const coordinateResult = await ocrAutofillService.default.processDocumentForAutofill(uploadedDocument.id, {
        minConfidence: 0.7,
        maxFields: 15,
        prioritizeSignatures: true,
        includeContext: true
      });

      if (coordinateResult.success && coordinateResult.detection.totalFields > 0) {
        console.log(`✅ Auto-detected ${coordinateResult.detection.totalFields} fields`);

        // Auto-create fields using the enhanced service
        const creationResult = await enhancedAutoFieldService.default.createFieldsFromCoordinateData(
          uploadedDocument.id,
          user.id,
          coordinateResult.detection.fields,
          {
            autoAssignToOwner: true,
            minConfidence: 0.7,
            maxFieldsToCreate: 15
          }
        );

        console.log(`✅ Auto-created ${creationResult.createdFields.length} fields`);

        // Store the created fields data for the review step
        setFieldsData({
          signatureFields: creationResult.createdFields.filter(f => f.type === 'signature').map(f => ({
            id: f.id,
            type: f.fieldType,
            page: f.coordinates.pageNumber,
            x: f.coordinates.x,
            y: f.coordinates.y,
            width: f.coordinates.width,
            height: f.coordinates.height,
            required: true,
            assignedTo: {
              userId: user.id,
              name: user.name,
              email: user.email
            }
          })),
          formFields: creationResult.createdFields.filter(f => f.type === 'form').map(f => ({
            id: f.id,
            fieldType: f.fieldType,
            page: f.coordinates.pageNumber,
            x: f.coordinates.x,
            y: f.coordinates.y,
            width: f.coordinates.width,
            height: f.coordinates.height,
            required: f.required || false,
            placeholder: f.label || f.placeholder,
            assignedTo: {
              userId: user.id,
              name: user.name,
              email: user.email
            }
          }))
        });

        // ✅ CACHE INVALIDATION
        invalidateDocumentQueries();

        // Navigate to review step
        setCurrentStep('review');
      } else {
        console.warn('⚠️ No fields detected, proceeding with manual field placement');
        setCurrentStep('signature-fields');
      }

    } catch (error) {
      console.error('❌ Error auto-placing fields:', error);
      alert('Failed to auto-place fields. Proceeding with manual field placement.');
      setCurrentStep('signature-fields');
    } finally {
      setIsProcessing(false);
    }
  };

  // Check which step we're in
  const isSignatureFieldsStep = currentStep === 'signature-fields' && uploadedDocument;
  const isReviewStep = currentStep === 'review' && uploadedDocument && fieldsData;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={handleBackToDashboard}
              className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Create New Document</h1>
          </div>
          
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-2">
              {steps.map((step, index) => {
                const status = getStepStatus(step.id);
                const Icon = step.icon;
                
                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                      status === 'complete' 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : status === 'current'
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'bg-white border-gray-300 text-gray-400'
                    }`}>
                      {status === 'complete' ? (
                        <CheckCircle size={24} />
                      ) : (
                        <Icon size={24} />
                      )}
                    </div>
                    <div className="ml-3 mr-8">
                      <div className={`text-sm font-medium ${
                        status === 'current' ? 'text-blue-600' : 
                        status === 'complete' ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {step.title}
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-4 ${
                        getStepStatus(steps[index + 1].id) === 'complete' || 
                        (getStepStatus(steps[index + 1].id) === 'current' && status === 'complete')
                          ? 'bg-green-500' 
                          : 'bg-gray-300'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Method Selection Step (Development Only) */}
          {currentStep === 'method-selection' && (
            <div className="p-8 flex flex-col items-center justify-center min-h-[70vh]">
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  <Plus className="text-blue-500" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Method</h2>
                <p className="text-gray-600">
                  Select how you want to create your document.
                </p>
                <div className="mt-2 px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full inline-block">
                  Development Mode
                </div>
              </div>
              
              <div className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Upload Option */}
                <button
                  onClick={() => handleMethodSelection('upload')}
                  className="group p-8 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <Upload className="text-blue-600" size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Document</h3>
                  <p className="text-gray-600 text-sm">
                    Upload an existing PDF, DOC, or DOCX file from your computer.
                  </p>
                </button>

                {/* Generate Option */}
                <button
                  onClick={() => handleMethodSelection('generate')}
                  className="group p-8 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all duration-200 text-left"
                >
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                    <Zap className="text-green-600" size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Generate Contract</h3>
                  <p className="text-gray-600 text-sm">
                    Use AI to generate a contract based on your requirements.
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Generate Preview Step */}
          {currentStep === 'generate-preview' && (
            <div className="p-8 flex flex-col items-center justify-center min-h-[70vh]">
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                  <Zap className="text-green-500" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Contract Generation</h2>
                <p className="text-gray-600 mb-4">
                  We will generate xyz contract for you based on your requirements.
                </p>
                <div className="mt-4 px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full inline-block">
                  Coming Soon - Development Preview
                </div>
              </div>
              
              <div className="w-full max-w-md space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">What we'll build:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• AI-powered contract generation</li>
                    <li>• Template selection</li>
                    <li>• Custom clause insertion</li>
                    <li>• Legal compliance checks</li>
                  </ul>
                </div>
                
                <button
                  onClick={handleBackToDashboard}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          )}

          {currentStep === 'upload' && (
            <div className="p-8 flex flex-col items-center justify-center min-h-[70vh]">
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  <Upload className="text-blue-500" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Your Document</h2>
                <p className="text-gray-600">
                  Select a document from your computer to get started. We support PDF, DOC, and DOCX files.
                </p>
              </div>

              <div className="w-full flex justify-center">
                <DocumentUploader
                  user={user}
                  onUploadSuccess={handleUploadSuccess}
                  // onCancel={handleBackToDashboard}
                  embedded={true}
                  onUploadOption={handleUploadOption}
                />
              </div>

            </div>
          )}

          {isSignatureFieldsStep && (
            <div className="min-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                    <FileText className="text-blue-500" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Place Fields & Signatures</h2>
                  <p className="text-gray-600">
                    Add signature fields for signing and form fields for data collection. Assign them to the people who need to complete them.
                  </p>
                </div>
              </div>
              
              <div className="flex-1 flex flex-col" style={{ minHeight: '600px' }}>
                <SignatureFieldPlacement
                  documentId={uploadedDocument.id}
                  onComplete={handleSignatureFieldsComplete}
                  // onCancel={handleBackToDashboard}
                  embedded={true}
                  enableFieldPollingOnMount={false}
                />
              </div>
            </div>
          )}

          {isReviewStep && (
            <div className="min-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                    <Eye className="text-blue-500" size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Presign</h2>
                  <p className="text-gray-600">
                    Review your document, sign your assigned fields, and send to other signers.
                  </p>
                </div>
              </div>
              
              <div className="flex-1 flex flex-col" style={{ minHeight: '600px' }}>
                <ReviewAndPresign
                  documentId={uploadedDocument.id}
                  fieldsData={fieldsData}
                  documentData={uploadedDocument}
                  onComplete={handleReviewComplete}
                  onCancel={handleBackToDashboard}
                />
              </div>
            </div>
          )}

          {isProcessing && (
            <div className="p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <Clock className="text-blue-500 animate-spin" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Document</h2>
              <p className="text-gray-600">Please wait while we set up your document...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewDocument;